import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog, DialogContent, Box, CircularProgress, Typography,
  Snackbar, Alert, IconButton, Button,
} from '@mui/material'
import { X, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { backendClient } from '../../../../api/backend'
import MetadataEditor from '../MetadataEditor'
import RichHtmlEditor from '../RichHtmlEditor'

/**
 * Flatten the nested metadata_json structure from BO pipeline
 * into the flat shape MetadataEditor expects.
 */
function flattenMetadata(raw) {
  if (!raw) return {}
  const doc = raw.document || {}
  const meta = raw.metadata || {}
  const gazette = doc.gazette_reference || {}
  const authority = doc.issuing_authority || {}
  const classification = doc.classification || {}

  return {
    title: doc.title || '',
    doc_number: doc.doc_number || '',
    issue_year: doc.issue_year || null,
    issue_date: doc.issue_date || '',
    publication_date: doc.publication_date || '',
    effective_date: doc.effective_date || '',
    last_update: doc.last_update || (doc.next_step === 'new' ? (doc.effective_date || '') : ''),
    gazette_info: {
      name: gazette.gazette_name || '',
      number: gazette.gazette_number || '',
      date: gazette.gazette_date || '',
    },
    legislation_type_code: doc.doc_type ? `MA_${doc.doc_type.toUpperCase()}` : '',
    authority_type: authority.authority_type || '',
    authority_name: authority.authority_name_fr || authority.authority_name_en || '',
    legal_domain: classification.legal_domain || '',
    date_hijri: doc.date_hijri || '',
    source_url: meta.source_url || '',
  }
}

/**
 * Rebuild the nested metadata_json from flat MetadataEditor state,
 * preserving any original fields that the form doesn't cover.
 */
function rebuildMetadata(flat, originalRaw) {
  const raw = originalRaw ? JSON.parse(JSON.stringify(originalRaw)) : {}
  if (!raw.document) raw.document = {}
  if (!raw.metadata) raw.metadata = {}

  // Document
  raw.document.title = flat.title
  raw.document.doc_number = flat.doc_number
  raw.document.issue_year = flat.issue_year
  raw.document.issue_date = flat.issue_date
  raw.document.publication_date = flat.publication_date
  raw.document.effective_date = flat.effective_date
  raw.document.last_update = flat.last_update
  raw.document.date_hijri = flat.date_hijri
  if (flat.legislation_type_code) {
    raw.document.doc_type = flat.legislation_type_code.replace(/^MA_/i, '').toLowerCase()
  }

  // Gazette
  raw.document.gazette_reference = {
    ...(raw.document.gazette_reference || {}),
    gazette_name: flat.gazette_info?.name || '',
    gazette_number: flat.gazette_info?.number || '',
    gazette_date: flat.gazette_info?.date || '',
  }

  // Authority
  raw.document.issuing_authority = {
    ...(raw.document.issuing_authority || {}),
    authority_type: flat.authority_type,
    authority_name_fr: flat.authority_name,
  }

  // Classification
  if (!raw.document.classification) raw.document.classification = {}
  raw.document.classification.legal_domain = flat.legal_domain

  // Metadata
  raw.metadata.source_url = flat.source_url

  return raw
}

export default function BODocumentPreview({ open, onClose, docId }) {
  const { t } = useTranslation()
  const { referenceData } = useSelector((s) => s.dataUpdate)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState(null)
  const [originalMetadataJson, setOriginalMetadataJson] = useState(null)
  const [error, setError] = useState(null)
  const [snackbar, setSnackbar] = useState(null)

  // Editable state — flat format for MetadataEditor
  const [metadata, setMetadata] = useState({})
  const [htmlContent, setHtmlContent] = useState('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (open && docId) {
      setLoading(true)
      setError(null)
      setPreview(null)
      setDirty(false)

      backendClient
        .getBoExtractedDocPreview(docId)
        .then((data) => {
          setPreview(data)
          setOriginalMetadataJson(data.metadata_json || {})
          setMetadata(flattenMetadata(data.metadata_json))
          setHtmlContent(data.html_content || '')
          setLoading(false)
        })
        .catch((err) => {
          setError(err.message || 'Failed to load preview')
          setLoading(false)
        })
    }
  }, [open, docId])

  const handleMetadataChange = useCallback((updated) => {
    setMetadata(updated)
    setDirty(true)
  }, [])

  const handleHtmlChange = useCallback((html) => {
    setHtmlContent(html)
    setDirty(true)
  }, [])

  const handleSave = async () => {
    if (!docId || !preview) return
    setSaving(true)
    try {
      const rebuilt = rebuildMetadata(metadata, originalMetadataJson)
      await backendClient.updateBoExtractedDoc(docId, {
        metadata_json: rebuilt,
        html_content: htmlContent,
        title: metadata.title || preview.title,
        doc_type: metadata.legislation_type_code
          ? metadata.legislation_type_code.replace(/^MA_/i, '').toLowerCase()
          : preview.doc_type,
        doc_number: metadata.doc_number || preview.doc_number,
      })
      setOriginalMetadataJson(rebuilt)
      setDirty(false)
      setSnackbar({ severity: 'success', message: t('du_bo_doc_saved', 'Document saved') })
    } catch (err) {
      setSnackbar({ severity: 'error', message: err.message || 'Save failed' })
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setPreview(null)
    setError(null)
    setDirty(false)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'background.default', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600, flex: 1, mr: 2 }}
          noWrap
        >
          {preview?.title || t('du_bo_document_preview', 'Document Preview')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={14} />}
            disabled={!dirty || saving}
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              backgroundColor: '#c9a96e',
              '&:hover': { backgroundColor: '#b8944f' },
              '&.Mui-disabled': { backgroundColor: 'rgba(201,169,110,0.3)' },
            }}
          >
            {t('du_save', 'Save')}
          </Button>
          <IconButton onClick={handleClose} size="small">
            <X size={18} />
          </IconButton>
        </Box>
      </Box>

      {/* Content — flex:1 fills remaining height */}
      <DialogContent sx={{ p: 0, flex: 1, display: 'flex', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress sx={{ color: '#c9a96e' }} />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : preview ? (
          <>
            {/* Left: Metadata form */}
            <Box
              sx={{
                width: 360, minWidth: 360, borderRight: 1, borderColor: 'divider',
                overflow: 'auto', p: 2.5, flexShrink: 0,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}
              >
                {t('du_bo_metadata', 'Metadata')}
              </Typography>
              <MetadataEditor
                metadata={metadata}
                onChange={handleMetadataChange}
                docType="legislation"
                referenceData={referenceData?.data}
                hideFields={['jurisdiction']}
              />
            </Box>

            {/* Right: HTML editor — fills remaining space */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
              <RichHtmlEditor
                value={htmlContent}
                onChange={handleHtmlChange}
                maxHeight="calc(100vh - 80px)"
              />
            </Box>
          </>
        ) : null}
      </DialogContent>

      {/* Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Dialog>
  )
}
